import React, { useState, useRef, useEffect, useContext, useCallback } from "react";
import { gisStateCoordinates, gisBlockCoordinatesData, gisPanchayatCoordinatesData, gisCoordinatesData, getThemeURL } from "../../../../Services/EndPoints";
import { toast } from "react-toastify";
import { getData } from "../../../../Services/Services";
import Loader from "react-spinner-loader";
import { ThemeContext } from "../../../..";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

// ─── Google Maps Loader ──────────────────────────────────────────────────────
const GOOGLE_MAPS_API_KEY = "AIzaSyDLzC-FhQmB8JS2H46OW46DNOEboBbrZVg";

let _gmapsLoaded = false;
let _gmapsLoading = false;
let _gmapsCallbacks = [];

function loadGoogleMaps(cb) {
  if (window.google && window.google.maps) { cb(); return; }
  _gmapsCallbacks.push(cb);
  if (_gmapsLoading) return;
  _gmapsLoading = true;
  const s = document.createElement("script");
  s.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry`;
  s.async = true;
  s.defer = true;
  s.onload = () => {
    _gmapsLoaded = true;
    _gmapsLoading = false;
    _gmapsCallbacks.forEach((fn) => fn());
    _gmapsCallbacks = [];
  };
  document.head.appendChild(s);
}

// ─── Facility Icon Configs ───────────────────────────────────────────────────
const facilityIconConfigs = {
  'Panchayat Bhawan':           { color: '#3b82f6', emoji: '🏛️', bgColor: '#dbeafe', label: 'Panchayat Bhawan' },
  'Flood Relief Camp':          { color: '#06b6d4', emoji: '⛺',  bgColor: '#cffafe', label: 'Flood Relief Camp' },
  'Animal Shelter':             { color: '#84cc16', emoji: '🐾',  bgColor: '#ecfccb', label: 'Animal Shelter' },
  'Goverment School':           { color: '#8b5cf6', emoji: '🏫', bgColor: '#ede9fe', label: 'Government School' },
  'Community Kitchen':          { color: '#f59e0b', emoji: '🍲', bgColor: '#fef3c7', label: 'Community Kitchen' },
  'Government Office Buliding': { color: '#64748b', emoji: '🏢', bgColor: '#f1f5f9', label: 'Govt Office' },
  'Helipad':                    { color: '#dc2626', emoji: '🚁', bgColor: '#fee2e2', label: 'Helipad' },
  'Hospital / PHC / HC':        { color: '#ef4444', emoji: '🏥', bgColor: '#fecaca', label: 'Hospital/PHC' },
  'ITBP Camp':                  { color: '#0ea5e9', emoji: '🛡️', bgColor: '#e0f2fe', label: 'ITBP Camp' },
  'Integrated Relief Camp':     { color: '#10b981', emoji: '🏕️', bgColor: '#d1fae5', label: 'Relief Camp' },
  'DEFAULT':                    { color: '#6b7280', emoji: '📍', bgColor: '#f3f4f6', label: 'Facility' },
};

function buildMarkerIcon(facilityCategory) {
  const cfg = facilityIconConfigs[facilityCategory] || facilityIconConfigs['DEFAULT'];
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44">
      <circle cx="22" cy="22" r="19" fill="${cfg.bgColor}" stroke="${cfg.color}" stroke-width="2.5"/>
      <text x="22" y="28" text-anchor="middle" font-size="18">${cfg.emoji}</text>
    </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: { width: 44, height: 44 },
    anchor: { x: 22, y: 44 },
  };
}

function geoJsonGeometryToGooglePaths(geometry) {
  const toLatLng = ([lng, lat]) => ({ lat, lng });
  if (geometry.type === "Polygon") {
    return geometry.coordinates.map((ring) => ring.map(toLatLng));
  }
  if (geometry.type === "MultiPolygon") {
    const paths = [];
    geometry.coordinates.forEach((polygon) =>
      polygon.forEach((ring) => paths.push(ring.map(toLatLng)))
    );
    return paths;
  }
  return [];
}

// ─── Custom Zoom + Fullscreen Controls ──────────────────────────────────────
const MapControls = ({ mapInstance }) => {
  const handleZoomIn = () => {
    if (mapInstance) mapInstance.setZoom((mapInstance.getZoom() || 7) + 1);
  };
  const handleZoomOut = () => {
    if (mapInstance) mapInstance.setZoom((mapInstance.getZoom() || 7) - 1);
  };

  const pillBtn = (label, onClick) => (
    <button
      onClick={onClick}
      style={{
        width: 40,
        height: 40,
        background: "transparent",
        border: "none",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 22,
        fontWeight: 300,
        color: "#1e293b",
        lineHeight: 1,
        transition: "background 0.15s",
        userSelect: "none",
      }}
      onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
      title={label === "+" ? "Zoom in" : "Zoom out"}
    >{label}</button>
  );

  return (
    <div style={{
      position: "absolute",
      bottom: 80,
      right: 10,
      zIndex: 1000,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 6,
    }}>
      
      {/* Zoom pill */}
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        background: "rgba(255,255,255,0.92)",
        backdropFilter: "blur(8px)",
        boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
        borderRadius: 50,
        overflow: "hidden",
        border: "1px solid rgba(0,0,0,0.08)",
        width: 40,
      }}>
        {pillBtn("+", handleZoomIn)}
        <div style={{ width: 24, height: 1, background: "#e2e8f0", margin: "0 auto" }} />
        {pillBtn("−", handleZoomOut)}
      </div>
    </div>
  );
};

// ─── DynamicMapLegend ────────────────────────────────────────────────────────
const DynamicMapLegend = ({
  visible, onClose, facilityCategories, facilityCounts,
  totalFacilities, onItemClick, isPopupOpen,
}) => {
  const { theme } = useContext(ThemeContext);
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeItem, setActiveItem] = useState(null);

  if (!visible) return null;

  const uniqueCategories = [...new Set(facilityCategories)];
  const legendItems = uniqueCategories
    .map((cat) => ({
      ...(facilityIconConfigs[cat] || facilityIconConfigs['DEFAULT']),
      category: cat,
      count: facilityCounts[cat] || 0,
      key: `facility-${cat}`,
    }))
    .filter((item, index, self) =>
      index === self.findIndex((i) => i.label === item.label)
    );

  const handleItemClick = (item) => {
    if (isPopupOpen) return;
    setActiveItem(item.key);
    setTimeout(() => setActiveItem(null), 2000);
    if (onItemClick) onItemClick(item.category);
  };

  return (
    <div style={{
      position: "absolute", top: "10px", left: "60px",
      zIndex: isPopupOpen ? 999 : 1000,
      backgroundColor: theme === "light" ? "#ffffff" : "#1f2a44",
      borderRadius: "8px",
      boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
      border: `1px solid ${theme === "light" ? "#e5e7eb" : "#2c3e50"}`,
      minWidth: "240px", maxWidth: "280px", maxHeight: "400px",
      overflow: "hidden", transition: "all 0.3s ease",
      color: theme === "light" ? "#333" : "#fff",
      pointerEvents: isPopupOpen ? "none" : "auto",
      opacity: isPopupOpen ? 0.3 : 1,
      filter: isPopupOpen ? "blur(2px)" : "none",
    }}>
      <div
        onClick={() => !isPopupOpen && setIsExpanded(!isExpanded)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "12px 16px", background: "linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%)", color: "white",
          cursor: isPopupOpen ? "default" : "pointer",
          fontSize: "14px", fontWeight: "600", userSelect: "none",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          🗺️ Map Legend
          <span style={{ backgroundColor: "rgba(255,255,255,0.25)", borderRadius: "10px", padding: "1px 7px", fontSize: "11px", fontWeight: "500" }}>
            {legendItems.length}
          </span>
        </span>
        {isExpanded ? <FaChevronDown size={14} /> : <FaChevronUp size={14} />}
      </div>

      {isExpanded && (
        <div style={{ padding: "12px", overflowY: "auto", maxHeight: "350px" }}>
          {legendItems.length > 0 ? (
            legendItems.map((item) => {
              const isActive = activeItem === item.key;
              return (
                <div
                  key={item.key}
                  onClick={() => handleItemClick(item)}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "8px 12px", borderRadius: "6px", marginBottom: "4px",
                    cursor: isPopupOpen ? "default" : "pointer", transition: "all 0.2s ease",
                    backgroundColor: isActive ? `${item.color}15` : "transparent",
                    border: isActive ? `1px solid ${item.color}40` : "1px solid transparent",
                  }}
                  onMouseEnter={(e) => { if (!isPopupOpen) e.currentTarget.style.backgroundColor = theme === "light" ? "#f3f4f6" : "#2c3e50"; }}
                  onMouseLeave={(e) => { if (!isPopupOpen) e.currentTarget.style.backgroundColor = isActive ? `${item.color}15` : "transparent"; }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>
                    <div style={{
                      width: "24px", height: "24px", borderRadius: "50%",
                      border: `2px solid ${item.color}`, backgroundColor: item.bgColor,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "12px", flexShrink: 0,
                    }}>{item.emoji}</div>
                    <span style={{ fontSize: "13px", color: theme === "light" ? "#374151" : "#fff", fontWeight: "500" }}>
                      {item.label} {item.count > 0 && `(${item.count})`}
                    </span>
                  </div>
                </div>
              );
            })
          ) : (
            <div style={{ padding: "16px", textAlign: "center", color: theme === "light" ? "#666" : "#ccc", fontSize: "13px" }}>
              No facilities to display
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Toggle Button ───────────────────────────────────────────────────────────
const ToggleButton = ({ isMapView, onToggle }) => {
  const { theme } = useContext(ThemeContext);
  return (
    <div style={{
      display: "flex", alignItems: "center",
      backgroundColor: theme === "light" ? "#cfc9c9ff" : "#1f2a44",
      borderRadius: "25px", padding: "4px",
      margin: "0px 20px 20px 0px", width: "fit-content",
    }}>
      <button onClick={() => onToggle(true)} style={{ padding: "8px 16px", borderRadius: "20px", border: "none", backgroundColor: isMapView ? "#007bff" : "transparent", color: "white", cursor: "pointer", transition: "all 0.3s ease", fontWeight: isMapView ? "bold" : "normal" }}>Map</button>
      <button onClick={() => onToggle(false)} style={{ padding: "8px 16px", borderRadius: "20px", border: "none", backgroundColor: !isMapView ? "#007bff" : "transparent", color: "white", cursor: "pointer", transition: "all 0.3s ease", fontWeight: !isMapView ? "bold" : "normal" }}>Table</button>
    </div>
  );
};

// ─── DataTable ───────────────────────────────────────────────────────────────
const DataTable = ({ resouceData }) => {
  const { theme } = useContext(ThemeContext);

  const getShortenedText = (strLength, fulltext) => {
    let text = fulltext.length > strLength ? fulltext.substring(0, 25) + "..." : fulltext;
    return <span title={fulltext}>{text}</span>;
  };

  const thStyle = { padding: "12px", textAlign: "left", fontWeight: "bold", borderBottom: "2px solid #ddd", color: "#fff" };
  const tdStyle = { padding: "12px", whiteSpace: "nowrap", color: theme === "light" ? "#333" : "#fff" };

  return (
    <div className="rd-table-container" style={{ width: "100%", overflow: "hidden", backgroundColor: theme === "light" ? "#ffffff" : "#1f2a44", borderRadius: "8px", border: "1px solid #ddd", display: "flex", flexDirection: "column" }}>
      <div style={{ overflow: "auto", flex: 1 }}>
        <table className="rd-table" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
            <tr style={{ background: "linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%)" }}>
              {["Resource Name","Resource Category","Activity","Region Name","Agency Name","Resource Facility Name","Agency Contact","Total Quantity","Damaged Quantity","Available Quantity","Resource Description"].map((h) => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody style={{ backgroundColor: theme === "light" ? "#ffffff" : "#1f2a44" }}>
            {resouceData && resouceData.map((resource) => {
              const contact = resource.resource_contact_map?.[0]?.contact_details || {};
              const contactText = contact.phone1
                ? `${contact.name || ""}${contact.name && contact.job_title_config?.value ? ", " : ""}${contact.job_title_config?.value || ""}${contact.name || contact.job_title_config?.value ? ", " : ""}${contact.phone1}`
                : resource.agency_details?.phone || "";
              const regionName = resource.resource_region_map?.[0]?.region_details?.name || "";
              return (
                <tr key={resource.id} style={{ borderBottom: "1px solid #ddd" }}>
                  <td style={tdStyle}>{resource.name}</td>
                  <td style={tdStyle}>{resource.rsrc_cat_id || ""}</td>
                  <td style={tdStyle}>{resource.rsrc_activity_id || ""}</td>
                  <td style={tdStyle}>{regionName}</td>
                  <td style={tdStyle}>{resource.agency_details?.name || ""}</td>
                  <td style={tdStyle}>{resource.facility_name || ""}</td>
                  <td style={tdStyle}>{contactText}</td>
                  <td style={tdStyle}>{resource.total_quantity || ""}</td>
                  <td style={tdStyle}>{resource.damaged_quantity !== undefined && resource.damaged_quantity !== null ? resource.damaged_quantity : ""}</td>
                  <td style={tdStyle}>{resource.available_quantity !== undefined && resource.available_quantity !== null ? resource.available_quantity : ""}</td>
                  <td style={{ padding: "12px", color: theme === "light" ? "#333" : "#fff" }}>{getShortenedText(15, resource.description || "")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Popup HTML builder ──────────────────────────────────────────────────────
function buildResourcePopupHTML(resource) {
  const facilityCategory = resource.facilityTypeCategory || "DEFAULT";
  const contactRows = (resource.resource_contact_map || []).map((contactMap) => {
    const c = contactMap.contact_details || {};
    return `<div style="margin-bottom:10px;padding:8px;background:#f8f9fa;border-radius:4px;">
      <div style="margin-bottom:3px;"><strong style="color:#7f8c8d;">Contact Name:</strong><span style="margin-left:5px;">${c.name || "NA"}</span></div>
      ${c.job_title_config?.value ? `<div style="margin-bottom:3px;"><strong style="color:#7f8c8d;">Job Title:</strong><span style="margin-left:5px;">${c.job_title_config.value}</span></div>` : ""}
      <div style="margin-bottom:3px;"><strong style="color:#7f8c8d;">Contact Phone:</strong><span style="margin-left:5px;">${c.phone1 || "NA"}</span></div>
    </div>`;
  }).join("");

  const facilityContactRows = (resource.facility_contact_details || []).map((c) =>
    `<div>
      <div style="margin-bottom:3px;"><strong style="color:#7f8c8d;">Contact Name:</strong><span style="margin-left:5px;">${c.name || "NA"}</span></div>
      <div style="margin-bottom:3px;"><strong style="color:#7f8c8d;">Contact Phone:</strong><span style="margin-left:5px;">${c.phone1 || "NA"}</span></div>
    </div>`
  ).join("");

  return `
    <div style="height:350px;overflow:auto;max-width:300px;font-family:Poppins,sans-serif;">
      <div style="padding:10px;border-bottom:2px solid #e0e0e0;margin-bottom:10px;background:#e3f2fd;border-radius:4px;border-left:4px solid #1976d2;">
        <h4 style="margin:0 0 10px 0;color:#495057;font-size:14px;font-weight:bold;">Resource Details</h4>
        <div style="margin-bottom:5px;"><strong style="color:#7f8c8d;">Resource:</strong><span style="margin-left:5px;">${resource.name}</span></div>
        <!-- <div style="margin-bottom:5px;"><strong style="color:#7f8c8d;">Category:</strong><span style="margin-left:5px;">${resource.rsrc_cat_id}</span></div>
        <div style="margin-bottom:5px;"><strong style="color:#7f8c8d;">Activity:</strong><span style="margin-left:5px;">${resource.rsrc_activity_id}</span></div>
        <div style="margin-bottom:5px;"><strong style="color:#7f8c8d;">Description:</strong><span style="margin-left:5px;">${resource.description || "NA"}</span></div> -->
        <div style="margin-top:10px;">
          <div><strong style="color:#7f8c8d;">Total Quantity:</strong><span style="margin-left:5px;">${resource.total_quantity}</span></div>
          <div><strong style="color:#7f8c8d;">Damaged Quantity:</strong><span style="margin-left:5px;color:#FF0000;">${resource.damaged_quantity || 0}</span></div>
          <div><strong style="color:#7f8c8d;">Available Quantity:</strong><span style="margin-left:5px;color:${resource.available_quantity > 0 ? "#27ae60" : "#e74c3c"};">${resource.available_quantity}</span></div>
          <div><strong style="color:#7f8c8d;">Already Deployed Quantity:</strong><span style="margin-left:5px;">${resource.deploy_quantity || 0}</span></div>
        </div>
      </div>
      <div style="padding:10px;border-bottom:${resource.resource_contact_map?.length ? "2px solid #e0e0e0" : "none"};margin-bottom:${resource.resource_contact_map?.length ? "10px" : "0"};background:#e3f2fd;border-radius:4px;border-left:4px solid #1976d2;">
        <h4 style="margin:0 0 10px 0;color:#495057;font-size:14px;font-weight:bold;">Agency Details</h4>
        <div style="margin-bottom:5px;"><strong style="color:#7f8c8d;">Agency Name:</strong><span style="margin-left:5px;">${resource.agency_details?.name || "NA"}</span></div>
        ${contactRows}
      </div>
      <div style="padding:10px;background:#e3f2fd;border-radius:4px;border-left:4px solid #1976d2;">
        <h4 style="margin:0 0 10px 0;color:#495057;font-size:14px;font-weight:bold;">Facility Details</h4>
        <div style="margin-bottom:5px;"><strong style="color:#7f8c8d;">Facility Name:</strong><span style="margin-left:5px;">${resource.facility_name || "NA"}</span></div>
        <div style="margin-bottom:5px;"><strong style="color:#7f8c8d;">Facility Category:</strong><span style="margin-left:5px;">${facilityIconConfigs[facilityCategory]?.label || facilityCategory}</span></div>
        <div style="margin-bottom:5px;"><strong style="color:#7f8c8d;">Facility Address:</strong><span style="margin-left:5px;">${resource.facility_address || "NA"}</span></div>
        ${facilityContactRows ? `<div style="margin-top:10px;padding:8px;background:#f8f9fa;border-radius:4px;">${facilityContactRows}</div>` : ""}
      </div>
    </div>`;
}

// ─── Main Component ──────────────────────────────────────────────────────────
const DeploymentResourceMap = ({ resouceData, shouldHighlight, incidentRegions }) => {
  const [loader, setLoader] = useState(false);
  const [isLayerMenuOpen, setIsLayerMenuOpen] = useState(false);
  const [activeBaseLayer, setActiveBaseLayer] = useState("terrain");
  const [isMapView, setIsMapView] = useState(true);
  const [showLegend, setShowLegend] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);
  const [facilityCategories, setFacilityCategories] = useState([]);
  const [facilityCounts, setFacilityCounts] = useState({});
  const [stateId, setStateId] = useState(null);
  const [isGmapsReady, setIsGmapsReady] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { theme } = useContext(ThemeContext);

  const mapDivRef = useRef(null);
  const mapContainerRef = useRef(null); // for fullscreen
  const mapRef = useRef(null);
  const polygonsRef = useRef([]);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);

  const layerTypeMap = { road: "roadmap", satellite: "satellite", terrain: "terrain", hybrid: "hybrid" };

  useEffect(() => { loadGoogleMaps(() => setIsGmapsReady(true)); }, []);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const handleFullscreen = useCallback(() => {
    const el = mapContainerRef?.current;
    if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen?.();
    else document.exitFullscreen?.();
  }, []);

  useEffect(() => {
    if (!isGmapsReady || !mapDivRef.current || mapRef.current) return;

    const map = new window.google.maps.Map(mapDivRef.current, {
      center: { lat: 34.5, lng: 76.5 },
      zoom: 7,
      minZoom: 4,
      mapTypeId: layerTypeMap[activeBaseLayer] || "terrain",
      disableDefaultUI: true,   // ← disable all default controls
      zoomControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      mapTypeControl: false,
    });

    mapRef.current = map;
    setMapInstance(map); // expose to MapControls
    infoWindowRef.current = new window.google.maps.InfoWindow();
    infoWindowRef.current.addListener("closeclick", () => setPopupOpen(false));

    setTimeout(() => {
      window.google.maps.event.trigger(map, "resize");
      map.setCenter({ lat: 34.5, lng: 76.5 });
    }, 0);

    const resizeObserver = new ResizeObserver(() => {
      if (mapRef.current) window.google.maps.event.trigger(mapRef.current, "resize");
    });
    resizeObserver.observe(mapDivRef.current);
    return () => resizeObserver.disconnect();
  }, [isGmapsReady]);

  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setMapTypeId(layerTypeMap[activeBaseLayer] || "terrain");
  }, [activeBaseLayer]);

  useEffect(() => {
    if (resouceData && resouceData.length > 0) {
      const categories = resouceData.map((r) => r.facilityTypeCategory).filter(Boolean);
      const counts = {};
      resouceData.forEach((r) => { const cat = r.facilityTypeCategory || "DEFAULT"; counts[cat] = (counts[cat] || 0) + 1; });
      setFacilityCategories(categories);
      setFacilityCounts(counts);
    } else {
      setFacilityCategories([]);
      setFacilityCounts({});
    }
  }, [resouceData]);

  useEffect(() => { getUserProfile(); });

  const getUserProfile = async () => {
    try {
      const profile = await getData(getThemeURL);
      if (profile && profile.length > 0) setStateId(profile[0].state_id);
    } catch (error) { console.error("Error fetching user profile:", error); }
  };

  useEffect(() => { if (!stateId || !isGmapsReady) return; mapStateCoordinates(); }, [stateId, isGmapsReady]);

  const mapStateCoordinates = async () => {
    setLoader(true);
    try {
      const res = await getData(gisStateCoordinates + String(stateId));
      if (res && res.length > 0) {
        const stateData = res[0];
        drawPolygon(JSON.parse(stateData.coordinates), { color: "#000000", fillColor: "transparent", fillOpacity: 0.2, weight: 2 }, `<b>State:</b> ${stateData.state_name}`);
      }
    } catch (error) { console.error("Error fetching state coordinates:", error); toast.error("Failed to load state coordinates"); }
    finally { setLoader(false); }
  };

  useEffect(() => {
    if (!isGmapsReady) return;
    if (shouldHighlight && incidentRegions && incidentRegions.length > 0) fetchAllPanchayatCoordinates(incidentRegions);
    if (shouldHighlight && resouceData && resouceData.length > 0) {
      const resource = resouceData[0];
      if (resource.facility_lat && resource.facility_lng && mapRef.current) {
        mapRef.current.panTo({ lat: resource.facility_lat, lng: resource.facility_lng });
        mapRef.current.setZoom(15);
      }
      const resourceGisId = resouceData[0].resource_region_map?.[0]?.region_details?.gis_id;
      if (resourceGisId) fetchBlockCoordinates(resourceGisId);
      const regionDetails = resouceData[0].resource_region_map?.[0]?.region_details;
      if (regionDetails?.zipcode) fetchDistrictCoordinates(regionDetails.zipcode);
    }
  }, [shouldHighlight, resouceData, incidentRegions, isGmapsReady]);

  useEffect(() => {
    if (!isGmapsReady || !mapRef.current) return;
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    if (!shouldHighlight || !resouceData) return;
    resouceData.forEach((resource) => {
      if (!resource.facility_lat || !resource.facility_lng) return;
      const facilityCategory = resource.facilityTypeCategory || "DEFAULT";
      const iconCfg = buildMarkerIcon(facilityCategory);
      const marker = new window.google.maps.Marker({
        position: { lat: resource.facility_lat, lng: resource.facility_lng },
        map: mapRef.current,
        icon: {
          url: iconCfg.url,
          scaledSize: new window.google.maps.Size(iconCfg.scaledSize.width, iconCfg.scaledSize.height),
          anchor: new window.google.maps.Point(iconCfg.anchor.x, iconCfg.anchor.y),
        },
      });
      marker.addListener("click", () => {
        infoWindowRef.current.setContent(buildResourcePopupHTML(resource));
        infoWindowRef.current.open(mapRef.current, marker);
        setPopupOpen(true);
      });
      markersRef.current.push(marker);
    });
  }, [resouceData, shouldHighlight, isGmapsReady]);

  const drawPolygon = useCallback((geometry, styleOpts, popupHTML) => {
    if (!mapRef.current || !window.google) return;
    const paths = geoJsonGeometryToGooglePaths(geometry);
    if (!paths.length) return;
    const poly = new window.google.maps.Polygon({
      paths, map: mapRef.current,
      strokeColor: styleOpts.color || "#000", strokeWeight: styleOpts.weight || 2,
      fillColor: styleOpts.fillColor || "transparent",
      fillOpacity: styleOpts.fillOpacity !== undefined ? styleOpts.fillOpacity : 0.2,
    });
    if (popupHTML) {
      poly.addListener("click", (e) => { infoWindowRef.current.setContent(popupHTML); infoWindowRef.current.setPosition(e.latLng); infoWindowRef.current.open(mapRef.current); setPopupOpen(true); });
      poly.addListener("mouseover", () => poly.setOptions({ strokeWeight: (styleOpts.weight || 2) + 1 }));
      poly.addListener("mouseout", () => poly.setOptions({ strokeWeight: styleOpts.weight || 2 }));
    }
    polygonsRef.current.push(poly);
    return poly;
  }, []);

  const fetchDistrictCoordinates = async (zipcode) => {
    try {
      const response = await getData(`${gisCoordinatesData}${Number(zipcode)}`);
      if (response && response.length > 0) {
        const d = response[0];
        drawPolygon(JSON.parse(d.coordinates), { color: "#FFA500", weight: 2.5, fillColor: "#FFA500", fillOpacity: 0.2 },
          `<div style="font-family:Poppins;max-width:250px;"><h6 style="margin:0 0 10px 0;padding:5px;background:#1a5cb8;color:white;text-align:center;font-size:14px;">Region</h6><div style="padding:5px;background:#e3f2fd;border-radius:4px;border-left:4px solid #1976d2;"><div><span style="font-weight:bold;color:#7f8c8d;">District:</span> ${d.dist_name || "NA"}</div></div></div>`
        );
      }
    } catch (error) { console.error("Error fetching District coordinates:", error); toast.error("Failed to load District coordinates"); }
  };

  const fetchBlockCoordinates = async (gisId) => {
    try {
      const response = await getData(`${gisBlockCoordinatesData}${gisId}`);
      if (response && response.length > 0) {
        const b = response[0];
        drawPolygon(JSON.parse(b.coordinates), { color: "#eb6c6cff", weight: 1.5, fillColor: "#eb6c6cff", fillOpacity: 0.15 },
          `<div style="font-family:Poppins;max-width:250px;"><h6 style="margin:0 0 10px 0;padding:5px;background:#1a5cb8;color:white;text-align:center;font-size:14px;">Incident Region</h6><div style="padding:5px;background:#e3f2fd;border-radius:4px;border-left:4px solid #1976d2;"><div><span style="font-weight:bold;color:#7f8c8d;">District:</span> ${b.dist_name || "NA"}</div><div><span style="font-weight:bold;color:#7f8c8d;">Block:</span> ${b.blk_name || "NA"}</div></div></div>`
        );
        if (mapRef.current && b.latitude && b.longitude) { mapRef.current.setCenter({ lat: b.latitude, lng: b.longitude }); mapRef.current.setZoom(10); }
      }
    } catch (error) { console.error("Error fetching Block coordinates:", error); toast.error("Failed to load Block coordinates"); }
  };

  const fetchAllPanchayatCoordinates = async (incidentRegions) => {
    try {
      const promises = incidentRegions.map(async (region) => {
        if (region.gis_id) {
          const response = await getData(`${gisPanchayatCoordinatesData}${region.gis_id}`);
          if (response && response.length > 0) return { ...response[0], incidentData: region };
        }
        return null;
      });
      const results = (await Promise.all(promises)).filter(Boolean);
      results.forEach((panchayatData) => {
        const incidentData = panchayatData.incidentData;
        const isAffected = incidentData?.code === "INCIDENT_REGION_AFFECT";
        const isWarning  = incidentData?.code === "INCIDENT_REGION_WARN";
        const styleColor = isAffected ? "#dc3545" : isWarning ? "#ffc107" : "#28a745";
        const styleFill  = isAffected ? "#f8d7da" : isWarning ? "#fff3cd" : "#d4edda";
        const statusText = isAffected ? "Affected" : isWarning ? "Warning" : "Normal";
        drawPolygon(JSON.parse(panchayatData.coordinates), { color: styleColor, weight: 3, fillColor: styleFill, fillOpacity: 0.6 },
          `<div style="font-family:Poppins;max-width:300px;"><h6 style="margin:0 0 10px 0;padding:8px;background:${styleColor};color:white;text-align:center;font-size:14px;border-radius:4px;">Panchayat Region - ${statusText}</h6><div style="padding:5px;background:${styleFill};border-radius:4px;border-left:4px solid ${styleColor};"><div><span style="font-weight:bold;color:#6c757d;">District:</span> ${panchayatData.dist_name || "NA"}</div><div><span style="font-weight:bold;color:#6c757d;">Block:</span> ${panchayatData.blk_name || "NA"}</div><div><span style="font-weight:bold;color:#6c757d;">Panchayat:</span> ${incidentData.name || "NA"}</div></div></div>`
        );
      });
      if (results.length > 0 && mapRef.current && results[0].latitude) {
        mapRef.current.setCenter({ lat: results[0].latitude, lng: results[0].longitude });
        mapRef.current.setZoom(12);
      }
    } catch (error) { console.error("Error fetching Panchayat coordinates:", error); toast.error("Failed to load Panchayat coordinates"); }
  };

  const handleLegendItemClick = (category) => {
    if (!mapRef.current || !resouceData) return;
    const categoryMarkers = resouceData.filter((r) => (r.facilityTypeCategory || "DEFAULT") === category && r.facility_lat && r.facility_lng);
    if (!categoryMarkers.length) return;
    if (categoryMarkers.length === 1) {
      mapRef.current.panTo({ lat: categoryMarkers[0].facility_lat, lng: categoryMarkers[0].facility_lng });
      mapRef.current.setZoom(15);
    } else {
      const bounds = new window.google.maps.LatLngBounds();
      categoryMarkers.forEach((m) => bounds.extend({ lat: m.facility_lat, lng: m.facility_lng }));
      mapRef.current.fitBounds(bounds, 50);
    }
  };

  const LegendToggleButton = ({ showLegend, onToggle }) => {
    if (showLegend) return null;
    return (
      <button onClick={onToggle} style={{ position: "absolute", top: "70px", left: "60px", zIndex: 999, backgroundColor: theme === "light" ? "#ffffff" : "#1f2a44", color: theme === "light" ? "#333" : "#fff", border: `1px solid ${theme === "light" ? "#e5e7eb" : "#2c3e50"}`, borderRadius: "8px", padding: "10px 16px", cursor: "pointer", fontSize: "13px", fontWeight: "500", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", display: "flex", alignItems: "center", gap: "8px" }}>
        <span>🗺️</span> Show Legend
      </button>
    );
  };

  return (
    <>
      <div><ToggleButton isMapView={isMapView} onToggle={setIsMapView} /></div>

      <div style={{ height: "600px", fontFamily: "Arial, sans-serif", position: "relative", marginTop: "-8px" }}>
        {loader && <Loader show={loader} type="body" stack="vertical" message="Loading Data" />}

        <div
          ref={mapContainerRef}
          style={{ width: "100%", height: isFullscreen ? "100vh" : "100%", position: "relative", display: isMapView ? "block" : "none" }}
        >
          <style>{`
            [data-gmap-fix] .gm-style img { max-width: none !important; max-height: none !important; object-fit: unset !important; }
            [data-gmap-fix] .gm-style canvas { max-width: none !important; max-height: none !important; }
          `}</style>

          <div ref={mapDivRef} data-gmap-fix="true" style={{ width: "100%", height: isFullscreen ? "100vh" : "600px", display: "block" }} />

          {/* ── Custom Map Controls (zoom) ── */}
          <MapControls mapInstance={mapInstance} />

          {/* ── Layer Switcher ── */}
          <div style={{ position: "absolute", top: "10px", right: "10px", zIndex: 1000 }}>
            <button
              onClick={() => setIsLayerMenuOpen(!isLayerMenuOpen)}
              style={{ width: "50px", height: "50px", backgroundColor: "white", border: "2px solid rgba(0,0,0,0.2)", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M2 12L12 17L22 12" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M2 17L12 22L22 17" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {isLayerMenuOpen && (
              <div style={{ position: "absolute", top: "60px", right: "0", backgroundColor: "white", borderRadius: "8px", boxShadow: "0 4px 16px rgba(0,0,0,0.2)", minWidth: "180px", overflow: "hidden", border: "1px solid rgba(0,0,0,0.1)" }}>
                {["road", "satellite", "terrain", "hybrid"].map((layer) => (
                  <div key={layer} onClick={() => { setActiveBaseLayer(layer); setIsLayerMenuOpen(false); }}
                    style={{ padding: "12px 16px", cursor: "pointer", backgroundColor: activeBaseLayer === layer ? "#e3f2fd" : "white", fontWeight: activeBaseLayer === layer ? "bold" : "normal", color: "#333", textTransform: "capitalize" }}
                    onMouseEnter={(e) => { if (activeBaseLayer !== layer) e.currentTarget.style.backgroundColor = "#f5f5f5"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = activeBaseLayer === layer ? "#e3f2fd" : "white"; }}
                  >
                    {layer === "road" ? "Road View" : layer === "satellite" ? "Satellite View" : layer === "terrain" ? "Terrain View" : "Hybrid View"}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Fullscreen Button (below Layer Switcher) ── */}
          <button
            onClick={handleFullscreen}
            title={isFullscreen ? "Exit Full Screen" : "Full Screen"}
            style={{
              position: "absolute",
              top: "70px",
              right: "10px",
              zIndex: 1000,
              width: 50,
              height: 50,
              borderRadius: 8,
              border: isFullscreen ? "2px solid #3b82f6" : "2px solid rgba(0,0,0,0.2)",
              background: isFullscreen ? "linear-gradient(135deg,#eff6ff,#dbeafe)" : "white",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: isFullscreen ? "0 2px 12px rgba(59,130,246,0.35)" : "0 2px 8px rgba(0,0,0,0.15)",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={e => (e.currentTarget.style.transform = "scale(1.08)")}
            onMouseLeave={e => (e.currentTarget.style.transform = "scale(1)")}
          >
            {isFullscreen ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
              </svg>
            )}
          </button>

          <LegendToggleButton showLegend={showLegend} onToggle={() => setShowLegend(true)} />

          <DynamicMapLegend
            visible={showLegend && !popupOpen} onClose={() => setShowLegend(false)}
            facilityCategories={facilityCategories} facilityCounts={facilityCounts}
            totalFacilities={resouceData?.length || 0}
            onItemClick={handleLegendItemClick} isPopupOpen={popupOpen}
          />
        </div>

        {!isMapView && (
          <div style={{ flex: 1 }}>
            <DataTable resouceData={resouceData} />
          </div>
        )}
      </div>
    </>
  );
};

export default DeploymentResourceMap;